import { useState, useEffect, useRef, memo, useCallback } from 'react';
import {
  Package,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Eye,
  Calendar,
  User,
  MessageSquare,
  MoreVertical,
  Check,
  X,
  RefreshCw,
  FileText,
  CreditCard,
  Building,
  Phone,
  Mail,
  Flag
} from 'lucide-react';
import { supabase, optimizedQuery, withCache, clearQueryCache } from '../../lib/supabase';
import { format, subDays, startOfDay, endOfDay, isToday, isYesterday, isThisWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Tipos
interface OrderItem {
  product_id: string;
  quantity: number;
  price: number;
  product_name?: string;
  product_image?: string;
}

interface Order {
  id: string;
  created_at: string;
  updated_at?: string;
  total_amount: number;
  shipping_cost?: number;
  status: 'pending' | 'pending_payment' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_method: 'card' | 'cash' | 'twoCards' | 'bankTransfer';
  delivery_method: 'delivery' | 'pickup';
  transfer_receipt_url?: string;
  assigned_to?: string;
  assigned_to_name?: string;
  internal_notes?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  customer_name: string;
  customer_company?: string;
  customer_phone?: string;
  customer_email?: string;
  item_count: number;
  items_preview?: OrderItem[];
}

interface Employee {
  id: string;
  name: string;
}

// Configuración de columnas Kanban simplificadas
const KANBAN_COLUMNS = [
  {
    id: 'nuevos',
    statuses: ['pending'],
    label: 'Nuevos',
    icon: Clock,
    color: 'bg-yellow-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  {
    id: 'verificando',
    statuses: ['pending_payment'],
    label: 'Verificando Pago',
    icon: CreditCard,
    color: 'bg-orange-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200'
  },
  {
    id: 'preparando',
    statuses: ['processing'],
    label: 'Preparando',
    icon: Package,
    color: 'bg-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  {
    id: 'enviados',
    statuses: ['shipped'],
    label: 'Enviados',
    icon: Truck,
    color: 'bg-purple-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  }
] as const;

// Columnas colapsables (menos frecuentes)
const COLLAPSIBLE_COLUMNS = [
  {
    id: 'entregados',
    statuses: ['delivered'],
    label: 'Entregados',
    icon: CheckCircle,
    color: 'bg-green-500'
  },
  {
    id: 'cancelados',
    statuses: ['cancelled'],
    label: 'Cancelados',
    icon: XCircle,
    color: 'bg-red-500'
  }
];

// Colores y etiquetas
const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  pending_payment: 'bg-orange-100 text-orange-800 border-orange-200',
  processing: 'bg-blue-100 text-blue-800 border-blue-200',
  shipped: 'bg-purple-100 text-purple-800 border-purple-200',
  delivered: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200'
};

const statusLabels: Record<string, string> = {
  pending: 'Nuevo',
  pending_payment: 'Verificando',
  processing: 'Preparando',
  shipped: 'Enviado',
  delivered: 'Entregado',
  cancelled: 'Cancelado'
};

const paymentStatusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-700'
};

const paymentStatusLabels: Record<string, string> = {
  pending: 'Pendiente',
  paid: 'Pagado',
  failed: 'Fallido',
  refunded: 'Reembolsado'
};

const priorityColors: Record<string, string> = {
  low: 'text-gray-400',
  normal: 'text-blue-400',
  high: 'text-orange-500',
  urgent: 'text-red-500'
};

const paymentMethodLabels: Record<string, string> = {
  card: 'Tarjeta',
  bankTransfer: 'Transferencia',
  cash: 'Efectivo',
  twoCards: 'Dos Tarjetas'
};

// Filtros de fecha predefinidos
const DATE_FILTERS = [
  { id: 'all', label: 'Todos' },
  { id: 'today', label: 'Hoy' },
  { id: 'yesterday', label: 'Ayer' },
  { id: 'week', label: 'Esta semana' },
  { id: 'month', label: 'Este mes' }
];

// Componente de tarjeta de pedido
const OrderCard = memo(({
  order,
  isSelected,
  onSelect,
  onClick
}: {
  order: Order;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onClick: (id: string) => void;
}) => {
  const hasReceipt = order.payment_method === 'bankTransfer' && order.transfer_receipt_url;

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border-2 transition-all cursor-pointer hover:shadow-md ${
        isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-100 hover:border-gray-200'
      }`}
    >
      {/* Header con checkbox y prioridad */}
      <div className="flex items-center justify-between p-3 border-b border-gray-50">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(order.id);
            }}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-600">#{order.id.slice(-6)}</span>
          {order.priority !== 'normal' && (
            <Flag className={`w-3 h-3 ${priorityColors[order.priority]}`} fill="currentColor" />
          )}
        </div>
        <div className="flex items-center gap-1">
          {hasReceipt && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
              📄
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full ${paymentStatusColors[order.payment_status]}`}>
            {order.payment_status === 'paid' ? '✓ Pagado' : paymentStatusLabels[order.payment_status]}
          </span>
        </div>
      </div>

      {/* Contenido principal - click para ver detalles */}
      <div className="p-3" onClick={() => onClick(order.id)}>
        {/* Cliente */}
        <div className="mb-2">
          <p className="font-medium text-gray-900 text-sm truncate">{order.customer_name}</p>
          {order.customer_company && (
            <p className="text-xs text-gray-500 truncate">{order.customer_company}</p>
          )}
        </div>

        {/* Info del pedido */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-base font-bold text-gray-900">
            Q{order.total_amount.toFixed(2)}
          </span>
          <span className="text-xs text-gray-500">
            {order.item_count} {order.item_count === 1 ? 'producto' : 'productos'}
          </span>
        </div>

        {/* Método de pago y fecha */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <CreditCard className="w-3 h-3" />
            {paymentMethodLabels[order.payment_method]}
          </span>
          <span>
            {isToday(new Date(order.created_at))
              ? format(new Date(order.created_at), 'HH:mm')
              : isYesterday(new Date(order.created_at))
                ? 'Ayer ' + format(new Date(order.created_at), 'HH:mm')
                : format(new Date(order.created_at), 'd MMM', { locale: es })
            }
          </span>
        </div>

        {/* Asignado a */}
        {order.assigned_to_name && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <User className="w-3 h-3" />
              {order.assigned_to_name}
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

// Componente de columna Kanban
const KanbanColumn = memo(({
  column,
  orders,
  selectedOrders,
  onSelectOrder,
  onOrderClick,
  isCollapsed = false
}: {
  column: typeof KANBAN_COLUMNS[number];
  orders: Order[];
  selectedOrders: Set<string>;
  onSelectOrder: (id: string) => void;
  onOrderClick: (id: string) => void;
  isCollapsed?: boolean;
}) => {
  const Icon = column.icon;
  const count = orders.length;

  return (
    <div className={`flex flex-col ${isCollapsed ? 'w-full' : 'min-w-[300px] w-[300px]'}`}>
      {/* Header de columna */}
      <div className={`flex items-center gap-2 p-3 rounded-t-lg ${column.bgColor} ${column.borderColor} border-b-2`}>
        <div className={`p-1.5 rounded-md ${column.color}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h3 className="font-semibold text-gray-800">{column.label}</h3>
        <span className={`ml-auto px-2 py-0.5 rounded-full text-sm font-medium ${column.color} text-white`}>
          {count}
        </span>
      </div>

      {/* Lista de pedidos */}
      <div className={`flex-1 p-2 space-y-2 ${column.bgColor} rounded-b-lg min-h-[200px] max-h-[calc(100vh-300px)] overflow-y-auto`}>
        {orders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <Icon className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">Sin pedidos</p>
          </div>
        ) : (
          orders.map(order => (
            <OrderCard
              key={order.id}
              order={order}
              isSelected={selectedOrders.has(order.id)}
              onSelect={onSelectOrder}
              onClick={onOrderClick}
            />
          ))
        )}
      </div>
    </div>
  );
});

// Componente principal
export default function OrdersTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFilter = searchParams.get('userId');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterUserName, setFilterUserName] = useState<string | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Selección para acciones en lote
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Secciones colapsables
  const [showDelivered, setShowDelivered] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);

  // Empleados para asignación
  const [employees, setEmployees] = useState<Employee[]>([]);

  // Refs para dropdowns
  const filtersRef = useRef<HTMLDivElement>(null);

  // Cargar pedidos
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Usar la vista optimizada
      let query = supabase
        .from('orders_kanban_view')
        .select('*')
        .order('created_at', { ascending: false });

      // Aplicar filtro por usuario si existe
      if (userIdFilter) {
        query = query.eq('user_id', userIdFilter);

        // Obtener nombre del usuario para mostrar en el banner
        const { data: userData } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', userIdFilter)
          .single();

        setFilterUserName(userData?.name || 'Usuario');
      } else {
        setFilterUserName(null);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Transformar datos
      const transformedOrders: Order[] = (data || []).map((order: any) => ({
        id: order.id,
        created_at: order.created_at,
        updated_at: order.updated_at,
        total_amount: order.total_amount,
        shipping_cost: order.shipping_cost || 0,
        status: order.status,
        payment_status: order.payment_status,
        payment_method: order.payment_method || 'card',
        delivery_method: order.delivery_method || 'delivery',
        transfer_receipt_url: order.transfer_receipt_url,
        assigned_to: order.assigned_to,
        assigned_to_name: order.assigned_to_name,
        internal_notes: order.internal_notes,
        priority: order.priority || 'normal',
        customer_name: order.customer_name || 'Cliente',
        customer_company: order.customer_company,
        customer_phone: order.customer_phone,
        customer_email: order.customer_email,
        item_count: order.item_count || 0,
        items_preview: order.items_preview
      }));

      setOrders(transformedOrders);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar pedidos');
    } finally {
      setLoading(false);
    }
  }, [userIdFilter]);

  // Cargar empleados
  const fetchEmployees = useCallback(async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, name')
      .or('is_admin.eq.true,admin_permissions.is.not.null');

    setEmployees(data || []);
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchEmployees();
  }, [fetchOrders, fetchEmployees]);

  // Filtrar pedidos
  const filteredOrders = orders.filter(order => {
    // Búsqueda
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        order.id.toLowerCase().includes(search) ||
        order.customer_name.toLowerCase().includes(search) ||
        (order.customer_company?.toLowerCase().includes(search)) ||
        (order.customer_email?.toLowerCase().includes(search));
      if (!matchesSearch) return false;
    }

    // Filtro de fecha
    if (dateFilter !== 'all') {
      const orderDate = new Date(order.created_at);
      const today = new Date();

      switch (dateFilter) {
        case 'today':
          if (!isToday(orderDate)) return false;
          break;
        case 'yesterday':
          if (!isYesterday(orderDate)) return false;
          break;
        case 'week':
          if (!isThisWeek(orderDate)) return false;
          break;
        case 'month':
          if (orderDate.getMonth() !== today.getMonth() ||
              orderDate.getFullYear() !== today.getFullYear()) return false;
          break;
      }
    }

    // Filtro de pago
    if (paymentFilter !== 'all' && order.payment_status !== paymentFilter) {
      return false;
    }

    return true;
  });

  // Agrupar por columnas
  const ordersByColumn = KANBAN_COLUMNS.reduce((acc, column) => {
    acc[column.id] = filteredOrders.filter(order =>
      column.statuses.includes(order.status as any)
    );
    return acc;
  }, {} as Record<string, Order[]>);

  // Pedidos en secciones colapsables
  const deliveredOrders = filteredOrders.filter(o => o.status === 'delivered');
  const cancelledOrders = filteredOrders.filter(o => o.status === 'cancelled');

  // Handlers
  const handleOrderClick = (orderId: string) => {
    navigate(`/admin/order/${orderId}`);
  };

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (columnId: string) => {
    const columnOrders = ordersByColumn[columnId] || [];
    const allSelected = columnOrders.every(o => selectedOrders.has(o.id));

    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      columnOrders.forEach(o => {
        if (allSelected) {
          newSet.delete(o.id);
        } else {
          newSet.add(o.id);
        }
      });
      return newSet;
    });
  };

  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedOrders.size === 0) return;

    const confirmed = window.confirm(
      `¿Cambiar ${selectedOrders.size} pedido(s) a "${statusLabels[newStatus]}"?`
    );

    if (!confirmed) return;

    try {
      const { data, error } = await supabase.rpc('bulk_update_order_status', {
        p_order_ids: Array.from(selectedOrders),
        p_new_status: newStatus
      });

      if (error) throw error;

      setSelectedOrders(new Set());
      fetchOrders();
    } catch (err) {
      console.error('Error updating orders:', err);
      alert('Error al actualizar pedidos');
    }
  };

  const handleRefresh = () => {
    clearQueryCache();
    fetchOrders();
  };

  // Click fuera para cerrar filtros
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando pedidos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <p className="text-red-700 font-medium">Error</p>
          <p className="text-red-600">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-2 text-red-600 hover:text-red-800 font-medium flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const clearUserFilter = () => {
    setSearchParams(params => {
      params.delete('userId');
      return params;
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Banner de filtro por usuario */}
      {userIdFilter && filterUserName && (
        <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-700">
              Mostrando pedidos de: <strong>{filterUserName}</strong>
            </span>
          </div>
          <button
            onClick={clearUserFilter}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
          >
            <X className="w-4 h-4" />
            Ver todos los pedidos
          </button>
        </div>
      )}

      {/* Header con filtros */}
      <div className="p-4 bg-white border-b sticky top-0 z-10">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          {/* Búsqueda */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por ID, cliente, empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Filtros y acciones */}
          <div className="flex items-center gap-2">
            {/* Filtro de fecha */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {DATE_FILTERS.map(filter => (
                <button
                  key={filter.id}
                  onClick={() => setDateFilter(filter.id)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    dateFilter === filter.id
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Filtro de pago */}
            <div className="relative" ref={filtersRef}>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 ${
                  paymentFilter !== 'all' ? 'border-blue-500 text-blue-600' : ''
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="text-sm">Pago</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {showFilters && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg p-2 z-20">
                  {[
                    { id: 'all', label: 'Todos' },
                    { id: 'pending', label: 'Pendiente' },
                    { id: 'paid', label: 'Pagado' },
                    { id: 'failed', label: 'Fallido' }
                  ].map(option => (
                    <button
                      key={option.id}
                      onClick={() => {
                        setPaymentFilter(option.id);
                        setShowFilters(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded text-sm ${
                        paymentFilter === option.id
                          ? 'bg-blue-50 text-blue-600'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Botón refrescar */}
            <button
              onClick={handleRefresh}
              className="p-2 border rounded-lg hover:bg-gray-50"
              title="Refrescar"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Barra de acciones en lote */}
        {selectedOrders.size > 0 && (
          <div className="mt-3 flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <span className="text-sm font-medium text-blue-700">
              {selectedOrders.size} pedido(s) seleccionado(s)
            </span>
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkStatusChange('processing')}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                Marcar Preparando
              </button>
              <button
                onClick={() => handleBulkStatusChange('shipped')}
                className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
              >
                Marcar Enviado
              </button>
              <button
                onClick={() => handleBulkStatusChange('delivered')}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
              >
                Marcar Entregado
              </button>
              <button
                onClick={() => handleBulkStatusChange('cancelled')}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Cancelar Pedidos
              </button>
              <button
                onClick={() => setSelectedOrders(new Set())}
                className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
              >
                Deseleccionar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Kanban principal */}
      <div className="flex-1 overflow-x-auto p-4">
        <div className="flex gap-4 min-h-full">
          {KANBAN_COLUMNS.map(column => (
            <KanbanColumn
              key={column.id}
              column={column}
              orders={ordersByColumn[column.id] || []}
              selectedOrders={selectedOrders}
              onSelectOrder={handleSelectOrder}
              onOrderClick={handleOrderClick}
            />
          ))}
        </div>
      </div>

      {/* Secciones colapsables */}
      <div className="p-4 border-t bg-gray-50">
        {/* Entregados */}
        <div className="mb-2">
          <button
            onClick={() => setShowDelivered(!showDelivered)}
            className="flex items-center gap-2 w-full p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors"
          >
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium">Entregados</span>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-sm rounded-full">
              {deliveredOrders.length}
            </span>
            <ChevronRight className={`w-5 h-5 ml-auto transition-transform ${showDelivered ? 'rotate-90' : ''}`} />
          </button>

          {showDelivered && deliveredOrders.length > 0 && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3 bg-white rounded-lg border">
              {deliveredOrders.slice(0, 8).map(order => (
                <div
                  key={order.id}
                  onClick={() => handleOrderClick(order.id)}
                  className="p-3 bg-green-50 rounded-lg border border-green-100 cursor-pointer hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">#{order.id.slice(-6)}</span>
                    <span className="text-sm font-bold">Q{order.total_amount.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">{order.customer_name}</p>
                  <p className="text-xs text-gray-400">
                    {format(new Date(order.created_at), 'd MMM', { locale: es })}
                  </p>
                </div>
              ))}
              {deliveredOrders.length > 8 && (
                <div className="flex items-center justify-center p-3 text-sm text-gray-500">
                  +{deliveredOrders.length - 8} más
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cancelados */}
        <div>
          <button
            onClick={() => setShowCancelled(!showCancelled)}
            className="flex items-center gap-2 w-full p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors"
          >
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="font-medium">Cancelados</span>
            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-sm rounded-full">
              {cancelledOrders.length}
            </span>
            <ChevronRight className={`w-5 h-5 ml-auto transition-transform ${showCancelled ? 'rotate-90' : ''}`} />
          </button>

          {showCancelled && cancelledOrders.length > 0 && (
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-3 bg-white rounded-lg border">
              {cancelledOrders.slice(0, 8).map(order => (
                <div
                  key={order.id}
                  onClick={() => handleOrderClick(order.id)}
                  className="p-3 bg-red-50 rounded-lg border border-red-100 cursor-pointer hover:bg-red-100 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">#{order.id.slice(-6)}</span>
                    <span className="text-sm font-bold">Q{order.total_amount.toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">{order.customer_name}</p>
                  <p className="text-xs text-gray-400">
                    {format(new Date(order.created_at), 'd MMM', { locale: es })}
                  </p>
                </div>
              ))}
              {cancelledOrders.length > 8 && (
                <div className="flex items-center justify-center p-3 text-sm text-gray-500">
                  +{cancelledOrders.length - 8} más
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
